import { scrubEventPayload } from "@reflecto/core";
import {
  type BrowserOptions,
  browserApiErrorsIntegration,
  captureException,
  dedupeIntegration,
  eventFiltersIntegration,
  globalHandlersIntegration,
  init,
  linkedErrorsIntegration,
} from "@sentry/browser";

/**
 * Sentry error tracking for the web PWA — privacy-hardened, errors only
 * (observability Stage 1). The invariant: the page renders DECRYPTED
 * notification content and clipboard payloads, so nothing captured here
 * may disclose that content, keys, or tokens to Sentry. Every option below
 * exists to serve that invariant. See docs/observability.md.
 *
 * Page-only: the web app's service worker (`src/sw/`) is a separate build
 * (`vite.sw.config.ts`) enforced by `swImportBoundary.test.ts` — this
 * module (and every import here) must never be reachable from `src/sw/`.
 * Unlike the extension, the web app has no Chrome Web Store constraint
 * banning SDK-internal loader/lazy-load code paths, so a standard
 * `Sentry.init()` is the right call here (the extension uses a manual
 * `BrowserClient` + `Scope` instead — see
 * `apps/extension/src/shared/observability.ts`).
 */

/**
 * Exported for the test suite (mocked-transport boot). Pure — does not
 * call `Sentry.init` itself.
 */
export function buildWebSentryOptions(dsn: string): BrowserOptions {
  return {
    dsn,
    release: `reflecto-web@${__APP_VERSION__}`,
    environment: import.meta.env.MODE === "production" ? "production" : "development",
    // Errors only — mirrors the server (Stage 0) and extension (Stage 1)
    // posture: no tracing, no spans, no transactions.
    tracesSampleRate: 0,
    // v10.54+ replacement for the deprecated sendDefaultPii. Every
    // category off: no user info, no cookies, no headers, no request/
    // response bodies, no query params, no gen-AI recording, no
    // stack-frame local variables (they can hold decrypted notification
    // strings or key material).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      queryParams: false,
      genAI: { inputs: false, outputs: false },
      stackFrameVariables: false,
    },
    // Function form is mandatory — the array form MERGES with Sentry's
    // defaults (which include breadcrumbsIntegration and
    // httpContextIntegration), silently reintroducing the two
    // integrations excluded below. Returning an explicit array from a
    // function replaces the defaults entirely; the `defaults` param is
    // ignored on purpose.
    //
    // breadcrumbsIntegration is EXCLUDED: it records console/DOM/fetch/
    // XHR/history breadcrumbs, and the page renders decrypted
    // notification and clipboard content — a click or console breadcrumb
    // could quote it verbatim.
    //
    // httpContextIntegration is EXCLUDED: it attaches the current page
    // URL to every event, and this app uses hash routes to carry
    // conversation identifiers (`#/c/:key`) — that URL is routing
    // metadata about a specific decrypted conversation, not safe to ship
    // off-device.
    integrations: () => [
      dedupeIntegration(),
      eventFiltersIntegration(),
      linkedErrorsIntegration(),
      globalHandlersIntegration(),
      browserApiErrorsIntegration(),
    ],
    beforeSend: (event) => scrubEventPayload(event),
    // Backstop — no integration in the set above produces breadcrumbs,
    // but drop any that appear (e.g. from a future SDK default change)
    // before they can reach an event.
    beforeBreadcrumb: () => null,
    // Noisy, browser-environment errors that carry no diagnostic value
    // and no privacy risk either way — not worth events or quota.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
  };
}

/**
 * Initialize Sentry for the page. Reads `VITE_SENTRY_DSN`; unset/empty
 * means Sentry stays off — no client, no instrumentation, and every
 * `captureError` call below becomes a no-op. Call once, early, from
 * `main.tsx`.
 */
export function initWebObservability(): void {
  // Same loose `as string | undefined` cast as `subscribeToWebPush`'s
  // `VITE_VAPID_PUBLIC_KEY` read (services/webPush.ts) — Vite's env
  // typing doesn't know about app-specific `VITE_*` vars.
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  init(buildWebSentryOptions(dsn));
}

/**
 * Capture an exception. No-op when observability was never initialized
 * (no DSN). Deliberately takes NO extra/context parameter — attaching
 * application data at capture sites is the leak vector this module
 * exists to close. Any future context addition requires a scrub-test
 * vector first (see `packages/core/src/scrub/`).
 */
export function captureError(error: unknown): void {
  captureException(error);
}
